import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import Button from 'components/partials/button/button';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import Loader from 'components/partials/loader/loader';
import Tabs from 'components/partials/tabs/tabs';

import AddRegimeModal from './components/add-regime-modal';
import AwardCalculationTabContent from './components/award-calculation-tab-content';
import RegimesSidebar from './components/regimes-sidebar';
import DefinitionTabContent from './components/definition-tab-content';
import PopulationsTabContent from './components/populations-tab-content';
import UnsavedChangesModal from './components/unsaved-changes-modal';

import useRegimeDraft from './hooks/use-regime-draft';

import type {
  RegimeItem as RegimeItemFromHook,
  RegimeGroup as RegimeGroupFromHook,
} from './hooks/use-regimes-sidebar-actions';

import {
  useRegimeGroups,
  useRegimes,
  useCustomLogic,
  useAcademicRanks,
} from '../../hooks/use-aid-regimes';
import { useAidModel } from '../../hooks/use-aid-model-details';

import { RegimeSelectionProvider } from './hooks/regime-selection-context';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { MATRIX_TABS } from './matrix-step.constants';

import Icons from 'assets/icons';

const TAB_VALUES = MATRIX_TABS.map((t) => t.value);

const getTabIcon = (tabValue: string, activeTab: string): React.JSX.Element => {
  const activeIndex = TAB_VALUES.indexOf(activeTab);
  const tabIndex = TAB_VALUES.indexOf(tabValue);

  if (tabValue === activeTab) {
    return <Icons.EditPencil customClass="w-4 h-4" />;
  }

  if (tabIndex < activeIndex) {
    return <Icons.Check customClass="w-4 h-4" />;
  }

  return <Icons.CircleDotted customClass="w-4 h-4" />;
};

interface MatrixStepProps {
  stepActionsSlotId: string;
}

type RegimeItem = RegimeItemFromHook & {
  customLogicIds?: (string | number)[];
};

type RegimeGroupItem = RegimeGroupFromHook;

const MatrixStep = forwardRef<unknown, MatrixStepProps>(
  ({ stepActionsSlotId }, ref) => {
    const [activeTab, setActiveTab] = useState<string>(MATRIX_TABS[0].value);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegimeId, setSelectedRegimeId] = useState<
      string | number | null
    >(null);
    const [customLogicRequested, setCustomLogicRequested] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [isSavingBeforeAction, setIsSavingBeforeAction] = useState(false);

    const { clientId, modelId } = useParams();
    const currentUser = useCurrentUser() as unknown as {
      data?: { oktaUserId?: string };
    };
    const userId = currentUser?.data?.oktaUserId;

    const regimeGroupsQuery = useRegimeGroups({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!userId && !!modelId,
    });
    const regimeGroups = (regimeGroupsQuery.data ?? []) as RegimeGroupItem[];
    const isGroupsLoading = regimeGroupsQuery.isLoading;

    const regimesQuery = useRegimes({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!userId && !!modelId,
    });
    const regimesList = (regimesQuery.data ?? []) as RegimeItem[];
    const isRegimesLoading = regimesQuery.isLoading;

    const modelQuery = useAidModel({
      userId: userId ?? '',
      clientId: clientId ?? '',
      modelId: modelId ?? '',
      enabled: !!userId && !!clientId && !!modelId,
    });
    const modelData = modelQuery.data as
      | { enrollmentType?: string }
      | undefined;

    const academicRanksQuery = useAcademicRanks({
      userId: userId ?? '',
      clientId: clientId ?? '',
      enrollmentType: modelData?.enrollmentType ?? '',
      enabled: !!userId && !!clientId && !!modelData?.enrollmentType,
    });
    const academicRanks = (academicRanksQuery.data ?? []) as unknown[];

    const customLogicQuery = useCustomLogic({
      userId: userId ?? '',
      clientId: clientId ?? '',
      enabled: customLogicRequested && !!userId && !!clientId,
    });
    const customLogicCategories = (customLogicQuery.data ?? []) as Array<{
      category: string;
      [key: string]: unknown;
    }>;
    const isCustomLogicLoading = customLogicQuery.isLoading;

    const isLoading = isGroupsLoading || isRegimesLoading;
    const hasData = regimeGroups.length > 0 && regimesList.length > 0;

    useEffect(() => {
      if (!regimesList.length) return;

      const selectedExists = regimesList.some((r) => r.id === selectedRegimeId);

      if (!selectedExists) {
        setSelectedRegimeId(regimesList[0].id);
      }
    }, [regimesList, selectedRegimeId]);

    const selectedRegime = useMemo(
      () => regimesList.find((r) => r.id === selectedRegimeId) ?? null,
      [regimesList, selectedRegimeId]
    );

    useEffect(() => {
      if ((selectedRegime?.customLogicIds?.length ?? 0) > 0) {
        setCustomLogicRequested(true);
      }
    }, [selectedRegime?.id]);

    const {
      awardType,
      calculatedBy,
      ranges,
      onAwardTypeChange,
      onCalculatedByChange,
      onRangesChange,

      rankIds,
      rankIdToDisplayNumber,
      getCell,
      updateCell,

      baselineSelected,
      toggleBaseline,
      activeCategoryIds,
      activeCategories,
      optionByCategory,
      toggleCategory,
      removeCategory,
      setOption,

      hasChanges,
      isSaving,
      handleSave,
      handleDiscard,
    } = useRegimeDraft({
      selectedRegime: (selectedRegime ?? undefined) as
        | Record<string, unknown>
        | null
        | undefined,
      academicRanks,
      customLogicCategories,
      userId,
      modelId,
    });

    useEffect(() => {
      if (stepActionsSlotId) {
        setPortalTarget(document.getElementById(stepActionsSlotId));
      }
    }, [stepActionsSlotId]);

    useImperativeHandle(ref, () => ({ save: handleSave, hasChanges }), [
      handleSave,
      hasChanges,
    ]);

    const hasChangesRef = useRef(hasChanges);

    hasChangesRef.current = hasChanges;

    const selectedRegimeIdRef = useRef(selectedRegimeId);

    selectedRegimeIdRef.current = selectedRegimeId;

    const guardAction = useCallback(
      (action: () => void, affectedRegimeId?: string | number): void => {
        const affectsCurrentRegime =
          affectedRegimeId === undefined ||
          affectedRegimeId === selectedRegimeIdRef.current;

        if (hasChangesRef.current && affectsCurrentRegime) {
          setPendingAction(() => action);
        } else {
          action();
        }
      },
      [],
    );

    const handleAddRegime = useCallback(
      (): void => guardAction(() => setIsModalOpen(true)),
      [guardAction],
    );

    const handleSelectRegime = useCallback(
      (regimeId: string | number): void => {
        guardAction(() => {
          setSelectedRegimeId(regimeId);
          setActiveTab(MATRIX_TABS[0].value);
        });
      },
      [guardAction],
    );

    const handleSaveAndContinue = useCallback(async () => {
      setIsSavingBeforeAction(true);
      try {
        await handleSave();
        pendingAction?.();
      } finally {
        setPendingAction(null);
        setIsSavingBeforeAction(false);
      }
    }, [handleSave, pendingAction]);

    const handleDiscardAndContinue = useCallback(() => {
      handleDiscard();
      pendingAction?.();
      setPendingAction(null);
    }, [handleDiscard, pendingAction]);

    const handleCloseUnsaved = useCallback(() => {
      setPendingAction(null);
    }, []);

    const selectionCtx = useMemo(
      () => ({ selectedRegimeId, onSelectRegime: handleSelectRegime }),
      [selectedRegimeId, handleSelectRegime]
    );

    const tabItems = useMemo(
      () =>
        MATRIX_TABS.map((tab) => ({
          ...tab,
          icon: getTabIcon(tab.value, activeTab),
        })),
      [activeTab]
    );

    const stepActions = portalTarget
      ? createPortal(
          <>
            <Button
              type="button"
              variant="reserve-text"
              size="sm"
              onClick={handleDiscard}
              disabled={!hasChanges}
              data-testid="discard-changes-button"
            >
              Discard changes
            </Button>
            <Button
              type="button"
              variant="brand-filled"
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
              data-testid="save-matrix-button"
            >
              Save
            </Button>
          </>,
          portalTarget
        )
      : null;

    const renderTabContent = (): React.JSX.Element | null => {
      if (!selectedRegime) return null;

      switch (activeTab) {
        case 'populations':
          return (
            <PopulationsTabContent
              baselineSelected={baselineSelected}
              toggleBaseline={toggleBaseline}
              activeCategoryIds={activeCategoryIds}
              activeCategories={activeCategories}
              optionByCategory={optionByCategory}
              toggleCategory={toggleCategory}
              removeCategory={removeCategory}
              setOption={setOption}
              customLogicCategories={customLogicCategories}
              onCustomLogicRequested={() => setCustomLogicRequested(true)}
              isCustomLogicLoading={isCustomLogicLoading}
            />
          );
        case 'definition':
          return (
            <DefinitionTabContent
              awardType={awardType}
              calculatedBy={calculatedBy}
              ranges={ranges}
              academicRanks={
                academicRanks as Array<{
                  id: string | number;
                  rank: string | number;
                }>
              }
              onAwardTypeChange={onAwardTypeChange}
              onCalculatedByChange={onCalculatedByChange}
              onRangesChange={onRangesChange}
            />
          );
        case 'award-calculation':
          return (
            <AwardCalculationTabContent
              ranges={ranges}
              calculatedBy={calculatedBy}
              rankIds={rankIds}
              rankIdToDisplayNumber={rankIdToDisplayNumber}
              getCell={getCell}
              updateCell={updateCell}
            />
          );
        default:
          return null;
      }
    };

    return (
      <div
        className="relative w-full max-w-full overflow-hidden"
        data-testid="matrix-step"
      >
        {stepActions}

        <Loader loading={isLoading} fullScreen={false} />

        {!isLoading && !hasData && (
          <EmptySpaceContent
            image={<Icons.EmptyImage customClass="size-32" />}
            text="No regimes have been added yet"
            linkShow
            linkText="Add regime"
            onClick={handleAddRegime}
          />
        )}

        {!isLoading && hasData && (
          <div className="flex w-full max-w-full min-w-0 h-[calc(100vh-22.5rem)] border border-navy-200 rounded-md overflow-hidden">
            <RegimeSelectionProvider value={selectionCtx}>
              <RegimesSidebar
                userId={userId ?? ''}
                modelId={modelId ?? ''}
                groups={regimeGroups}
                regimes={regimesList}
                onAddRegime={handleAddRegime}
                onGuardAction={guardAction}
              />
            </RegimeSelectionProvider>

            <div className="w-0 flex-1 flex flex-col min-w-0 pl-2 overflow-hidden">
              <Tabs
                items={tabItems}
                activeTab={activeTab}
                onSelect={setActiveTab}
                data-testid="matrix-tabs"
              />
              <div className="flex-1 min-h-0 overflow-auto scrollbar-thin">
                {renderTabContent()}
              </div>
            </div>
          </div>
        )}

        {isModalOpen && (
          <AddRegimeModal
            userId={userId ?? ''}
            modelId={modelId ?? ''}
            existingGroups={regimeGroups}
            onClose={() => setIsModalOpen(false)}
            onSuccess={() => setIsModalOpen(false)}
          />
        )}

        <UnsavedChangesModal
          open={pendingAction !== null}
          loading={isSavingBeforeAction}
          onSaveAndContinue={handleSaveAndContinue}
          onDiscardAndContinue={handleDiscardAndContinue}
          onClose={handleCloseUnsaved}
        />
      </div>
    );
  }
);

MatrixStep.displayName = 'MatrixStep';

export default MatrixStep;
